import { Accordion, Text } from '@medly-components/core';
import { SvgIcon } from '@medly-components/icons';
import styled, { css } from 'styled-components';

import { NavItemStyledProps } from './types';
import { mapTourActiveElementStyles } from 'pages/Map/components/MapTour/MapTour.styled';

// This function is used to identify the color of the text, icon and background of the item based on the state of the nav item
const getColorStyle = ({ navItem, state }: NavItemStyledProps & { state: 'default' | 'hovered' | 'pressed' | 'active' }) => css`
    background-color: ${navItem.bgColor[state]};
    ${SvgIcon} {
        * {
            fill: ${navItem.icon.color[state]};
        }
    }
    & > ${/* sc-selector */ Text.Style}, ${/* sc-selector */ Accordion.Header.Style} > ${/* sc-selector */ Text.Style} {
        color: ${navItem.text.color[state]};
    }
`;

// This is the style for the left border of the nav item, when it is active
const leftBorder = css`
    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: 0.4rem;
        background-color: ${({ theme }) => theme.sideNav.navItem.icon.color.active};
    }
`;

const mapTourActiveStyles = css`
    ${mapTourActiveElementStyles}
    background: transparent;

    &&& {
        border-radius: 0.8rem;
        padding-left: 1rem;
        margin-left: -1rem;
    }
`;

export const NavItemStyled = styled('li').attrs(({ theme: { sideNav } }) => ({ ...sideNav }))<
    NavItemStyledProps & { isMapTourActiveElement: boolean }
>`
    cursor: pointer;
    position: relative;
    box-sizing: border-box;
    justify-items: flex-start;
    align-items: center;
    user-select: none;
    transition: all 100ms ease-out;
    display: flex;
    min-height: ${({ navItem }) => navItem.minHeight};
    padding-left: ${({ isExpanded, isHovered }) => (isExpanded || isHovered) && '1.2rem'};
    border-radius: ${({ isExpanded, isHovered }) => (isExpanded || isHovered ? `0 2.4rem 2.4rem 0` : '50%')};
    margin-bottom: ${({ isHovered, isExpanded }) => (isHovered || isExpanded ? '2.4rem' : '0.8rem')};
    margin-left: ${({ isExpanded, isHovered }) => !isExpanded && !isHovered && '1.2rem'};
    margin-right: ${({ isHovered, isExpanded }) => (isHovered || isExpanded ? '1.6rem' : '1.2rem')};
    & > ${SvgIcon} {
        overflow: visible;
        align-self: flex-start;
        border-radius: ${({ navItem }) => navItem.borderRadius};
        font-size: 2.4rem;
        padding: 1.2rem;
    }

    ${Text.Style} {
        font-size: 1.6rem;
        padding-top: 0.1rem;
        overflow: hidden;
        justify-self: left;
        white-space: nowrap;
        text-overflow: ellipsis;
        transition: opacity 200ms ease-out, color 100ms ease-out;
        opacity: ${({ isHovered, isExpanded }) => (isHovered || isExpanded ? 1 : 0)};
    }
    ${({ isActive, isExpanded, isHovered }) => isActive && (isExpanded || isHovered) && leftBorder};
    ${props => getColorStyle({ ...props, state: props.isActive ? 'active' : 'default' })};

    &:hover {
        ${props => !props.isActive && getColorStyle({ ...props, state: 'hovered' })};
    }
    &:active {
        ${props => !props.isActive && getColorStyle({ ...props, state: 'pressed' })};
    }
    &::after {
        content: '';
        width: calc(100% - ${({ theme }) => theme.spacing.M3});
        position: absolute;
        border-top: 0.1rem solid ${({ theme }) => theme.colors.grey[900]};
        margin: 0 auto;
        bottom: -1.2rem;
        left: 0;
        right: 0;
        display: ${({ isHovered, isExpanded }) => (isHovered || isExpanded ? 'block' : 'none')};
        @media (max-width: ${({ theme }) => theme.breakpoints.M.min}px) {
            width: calc(100% - ${({ theme }) => theme.spacing.M1});
            left: initial;
        }
    }
    &:last-child {
        &::after {
            display: none;
        }
    }
    @media (max-width: ${({ theme }) => theme.breakpoints.M.min}px) {
        margin-right: 2.4rem;
    }
    ${({ isMapTourActiveElement }) => isMapTourActiveElement && mapTourActiveStyles}
`;
